import React, { useEffect, useRef, useState } from "react";

import {
  AnyFilterExpressionInput,
  SortInput,
  TablesDetailsInput,
  useUpdateTableDetailsForUserMutation
} from "gql/generated/types";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Toolbar } from "primereact/toolbar";
import { exportExcel, findObjectVal, formatDateString } from "utils/utils";
import useProjectContext from "../hooks/useProjectContext";
import { TableOptions, TableColumn, MdmTableName, MdmContainerName } from "../utils/types";
import { formatSort, userTableDetails } from "../utils/utils";
import TableFilters from "./TableFilters";

type LazyTableProps = {
  data: any[];
  tableName: MdmTableName;
  containerName: MdmContainerName;
  columns: TableColumn<any>[];
  loading: boolean;
  setTableOptions: React.Dispatch<React.SetStateAction<TableOptions>>;
  tableOptions: TableOptions;
  totalItems: number;
  fetchData: (
    take?: number | null,
    skip?: number | null,
    sort?: SortInput[] | null,
    filters?: AnyFilterExpressionInput[] | null,
    cacheAndNetwork?: boolean,
    filter?: AnyFilterExpressionInput | null
  ) => Promise<any>;
  resetFilters: () => void;
  buttons?: JSX.Element;
  onSearch?: (value: string) => void;
};

const LazyTable: React.FC<LazyTableProps> = ({
  data,
  tableName,
  containerName,
  columns,
  loading,
  setTableOptions,
  tableOptions,
  totalItems,
  fetchData,
  resetFilters,
  buttons,
  onSearch,
}) => {
  const [viewFilters, setViewFilters] = useState(false);
  const [filtered, setFiltered] = useState(false);
  const dt = useRef<DataTable>(null);
  const { userSettings, project } = useProjectContext();
  const tableDetails = userTableDetails(userSettings, tableName);

  const [updateTableDetails] = useUpdateTableDetailsForUserMutation();

  useEffect(() => {
    if (tableOptions.internalFilters.length > 0) {
      setViewFilters(true);
      setFiltered(true);
    } else {
      setFiltered(false);
    }
  }, [tableOptions]);

  const handleExportExcel = async () => {
    const sort = formatSort(tableOptions.sortField, tableOptions.sortOrder, tableName, project?.mdmProjectById.configuration);
    const response: Record<string, { items: any[]}> = await fetchData(0, 0, sort)
    const tableData = Object.values(response)[0]?.items?.map(row => {
      const newData: any = {};
      columns.forEach(col => {
        if (col.hideInFile || col.hidden) return;
        newData[col.header] = col.field ? formatDateString(findObjectVal(row, col.field)?.toString()) : col.body;
      });
      return newData;
    });

    exportExcel(tableData, tableName);
  };

  const exportCSV = async () => {
    const sort = formatSort(tableOptions.sortField, tableOptions.sortOrder, tableName, project?.mdmProjectById.configuration);
    await fetchData(0, 0, sort)
    dt.current?.exportCSV({ selectionOnly: false });
  };

  const onPage = (e: DataTablePFSEvent) => {
    const tableDetailsInput: TablesDetailsInput = {
      table: tableName,
      rows: e.rows,
      columnOrders: tableDetails?.columnOrders || [],
    };
    if (tableDetails?.rows !== e.rows) {
      updateTableDetails({
        variables: { tableDetails: tableDetailsInput },
      });
    }
    setTableOptions(prev => ({ ...prev, first: e.first, page: e.page, rows: e.rows }));
    fetchData(e.rows, e.first);
  };

  const onSort = (e: DataTablePFSEvent) => {
    const sort = formatSort(e.sortField, e.sortOrder, tableName, project?.mdmProjectById.configuration);
    setTableOptions(prev => ({ ...prev, sortField: e.sortField, sortOrder: e.sortOrder, first: 0, reqSortField: sort[0].property }));
    fetchData(tableDetails?.rows , 0, sort);
  };

  return (
    <div className="card p-0 sm:p-3">
      <Toolbar
        left={
          <div className="flex gap-3">
            {onSearch && (
              <SearchInput onSearch={onSearch} tableOptions={tableOptions} />
            )}
            <Button
              title="Filters"
              type="button"
              icon={filtered ? "pi pi-filter-fill" : "pi pi-filter"}
              className="p-button-outlined w-4rem"
              onClick={() => setViewFilters(prev => !prev)}
            />
            <Button
              title="Reset filters"
              icon="pi pi-filter-slash"
              className="p-button-outlined p-button-danger w-4rem"
              onClick={resetFilters}
            />
          </div>
        }
        right={
          <div className="flex gap-3">
            <Button
              type="button"
              icon="pi pi-file"
              className="p-button-outlined p-button-warning"
              title="Export to CSV"
              onClick={exportCSV}
            />
            <Button
              type="button"
              icon="pi pi-file-excel"
              className="p-button-outlined p-button-warning"
              title="Export to Excel"
              onClick={handleExportExcel}
            />
            {buttons}
          </div>
        }
        className="gap-3"
      />

      <DataTable
        header={
          <div
            className={`${viewFilters ? "max-h-90rem" : "max-h-0"} overflow-hidden transition-all transition-duration-500 p-0`}
          >
            <TableFilters
              container={containerName}
              fetchData={fetchData}
              tableName={tableName}
              tableOptions={tableOptions}
              setTableOptions={setTableOptions}
            />
          </div>
        }
        ref={dt}
        value={data}
        stripedRows
        paginator
        rows={tableOptions.rows}
        reorderableColumns
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        loading={loading}
        size="small"
        lazy
        totalRecords={totalItems}
        className="overflow-x-auto lazy-table"
        first={tableOptions.first || 0}
        sortField={tableOptions.sortField}
        sortOrder={tableOptions.sortOrder}
        onPage={onPage}
        onSort={onSort}
      >
        {columns.map(col => (
          <Column key={col.header} {...col} />
        ))}
      </DataTable>
    </div>
  );
};

const SearchInput: React.FC<{
  onSearch: (value: string) => void;
  tableOptions: TableOptions;
}> = ({ onSearch, tableOptions }) => {
  const [search, setSearch] = useState("");

  useEffect(() => {
      setSearch(tableOptions.search);
  }, [tableOptions]);

  return (
    <div className="p-inputgroup">
      <InputText
        value={search ?? ""}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search..."
        onKeyUp={e => e.key === "Enter" && onSearch(search)}
      />
      <Button className="p-inputgroup-addon" icon="pi pi-search" onClick={() => onSearch(search)} />
    </div>
  );
};

export default LazyTable;
